import {
  useContext, useState
} from "react";
import {DrupalNode} from "next-drupal";
import {
  Dictionary,
  DrupalProductNode,
  DrupalSearchApiResponseMeta, WorkflowType
} from "@/types/hygiena-types";
import {NodeWorkflow} from "../node--workflow";

import dynamic from "next/dynamic";
import {DictionaryContext} from "@/context/dictionary-context";
import {ProductSectionSampleCollection} from "@/components/organisms/product-sections";
const ProductSectionDivider = dynamic(() => import("../../../molecules/product-section-divider").then((mod) => mod.ProductSectionDivider));
const ProductSectionEquipment = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionEquipment));
const ProductSectionInstruments = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionInstruments));
const ProductSectionAutomation = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionAutomation));
const ProductSectionMonitoring = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionMonitoring));
const ProductSectionSoftware = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionSoftware));
const ProductSectionRealAdditionals = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionRealAdditionals));
const ProductSectionMedia = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionMedia));
const ProductSectionCompatibles = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionCompatibles));
const ProductSectionAccessories = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionAccessories));
const ProductSectionTrainingVideos = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionTrainingVideos));
const ProductSectionRelatedProducts = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionRelatedProducts));
const ProductSectionRelatedNews = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionRelatedNews));
const ProductSectionExtractions = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionExtractions));
const ProductSectionResources = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionResources));
const ProductSectionKeyBenefits = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionKeyBenefits));
const ProductSectionTLDRPodcasts = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionTLDRPodcasts));
const ProductSectionLayoutComponents = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionLayoutComponents));
const ProductSectionBuyOnline = dynamic(() => import("../../../organisms/product-sections").then((mod) => mod.ProductSectionBuyOnline));

/**
 * The Full Page product display for Products, this component should be loaded in using dynamic().
 *
 * @param node
 *   The product node.
 * @param additionalContent
 *   Additional content.
 * @param props
 *   Additional properties.
 * @constructor
 */
export function ContentProduct({node, additionalContent, ...props}: {
  node: DrupalProductNode,
  additionalContent?: {
    relatedNews?: DrupalNode[]
    compatibleProducts?: DrupalProductNode[],
    compatibleMeta?: DrupalSearchApiResponseMeta,
  },
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const sectionHeaderSize = node?.field_product_workflow?.field_workflow_items?.length ? 3 : 2;
  const subProductHeaderSize = sectionHeaderSize === 3 ? 4 : 3;
  const [activeWorkflowSections, setActiveWorkflowSections] = useState<string[]>(getActiveWorkflowSections());
  const isFoodproofWorkflow = node?.field_product_brand?.name === "foodproof";

  /**
   * Gets the active workflow sections for this product.
   *
   * @return string[]
   */
  function getActiveWorkflowSections(): string[] {
    const sections: string[] = [];
    if (!node?.field_product_workflow?.field_workflow_items?.length) {
      return sections;
    }
    for (const item of node.field_product_workflow.field_workflow_items) {
      if (Array.isArray(item.field_workflow_item_product_type)) {
        item.field_workflow_item_product_type.map(item => {sections.push(item)});
      }
      else if (item.field_workflow_item_product_type) {
        sections.push(item);
      }
    }
    return sections;
  }

  return (
    // Main product displayed in the hero.
    <article className="pt-6 " {...props}>
      {/* Key Benefits */}
      <ProductSectionKeyBenefits node={node}/>
      {/* TLDR Podcast */}
      <ProductSectionTLDRPodcasts node={node}/>
      {/* Layout Components */}
      <ProductSectionLayoutComponents node={node}/>
      {/* Workflow */}
      <ProductSectionDivider className="!mb-0">
        <NodeWorkflow
          node={node}
          workflowType={node?.field_product_workflow_type}
          isFoodproofWorkflow={isFoodproofWorkflow}
        />
      </ProductSectionDivider>
      {/* Equipment */}
      {!activeWorkflowSections.includes(WorkflowType.Equipment) && (
        <ProductSectionEquipment
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
        />
      )}
      {/* Instruments */}
      {!activeWorkflowSections.includes(WorkflowType.Instruments) && (
        <ProductSectionInstruments
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
          isFoodproofWorkflow={isFoodproofWorkflow}
        />
      )}
      {/* Automation */}
      {!activeWorkflowSections.includes(WorkflowType.Automation) && (
        <ProductSectionAutomation
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
        />
      )}
      {/* Monitoring Systems */}
      {!activeWorkflowSections.includes(WorkflowType.Monitoring) && (
        <ProductSectionMonitoring
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
        />
      )}
      {/* Sample Collection */}
      {!activeWorkflowSections.includes(WorkflowType.SampleCollection) && (
        <ProductSectionSampleCollection
          node={node}
          sectionHeaderSize={sectionHeaderSize}
        />
      )}
      {/* Software */}
      {!activeWorkflowSections.includes(WorkflowType.Software) && (
        <ProductSectionSoftware
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
        />
      )}
      {/* Additionals */}
      {!activeWorkflowSections.includes(WorkflowType.Additionals) && (
        <ProductSectionRealAdditionals
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
        />
      )}
      {/* Extraction*/}
      {!activeWorkflowSections.includes(WorkflowType.Extractions) && (
        <ProductSectionExtractions
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
        />
      )}
      {/* Enrichment */}
      {!activeWorkflowSections.includes(WorkflowType.Media) && (
        <ProductSectionMedia
          node={node}
          sectionHeaderSize={sectionHeaderSize}
          subProductHeaderSize={subProductHeaderSize}
        />
      )}
      {/* Compatible Products */}
      {!activeWorkflowSections.includes(node?.field_comp_workflow_type) && (
        <ProductSectionCompatibles node={node} additionalContent={additionalContent} />
      )}
      {/* Accessories */}
      <ProductSectionAccessories node={node}/>
      {/* Document Library */}
      <ProductSectionResources node={node}/>
      {/* Buy Online */}
      <ProductSectionBuyOnline node={node}/>
      {/* Training Videos */}
      <ProductSectionTrainingVideos node={node}/>
      {/* Related Products */}
      <ProductSectionRelatedProducts node={node}/>
      {/* Related News */}
      <ProductSectionRelatedNews relatedNews={additionalContent?.relatedNews} />
    </article>
  );
}
